/**
 * Smart-plug outlet control API client. Talks to
 * `POST /api/plugs/{scu}/outlet/{n}/{action}`.
 *
 * `action` is 'reset' (off → wait → on), 'on', or 'off'. The reset
 * round-trip takes ~6s; the UI should disable the button while in
 * flight. Failures (cloud down, auth, device offline) come back as
 * `success=false` with a short `message` the UI can show inline.
 */

import { ApiError, apiBase } from "./apiClient";

export type PlugAction = "reset" | "on" | "off";

export interface PlugCommandResponse {
  command_id: number;
  scu_name: string;
  outlet_num: number;
  action: PlugAction;
  success: boolean;
  message: string;
  http_status: number | null;
  plug: { friendly_name: string | null };
}

export class PlugsApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "PlugsApiError";
  }
}

function baseUrl(): string {
  // This route needs the parsed body even on non-OK responses (the failure
  // message rides in `body.message`), so it keeps its own fetch rather than
  // going through apiFetch; only the base-URL resolution is shared.
  try {
    return apiBase();
  } catch (e) {
    throw e instanceof ApiError ? new PlugsApiError(e.message, e.status) : e;
  }
}

export async function plugOutletAction(
  token: string,
  scu: string,
  outletNum: 1 | 2,
  action: PlugAction,
): Promise<PlugCommandResponse> {
  const path = `/api/plugs/${encodeURIComponent(scu)}/outlet/${outletNum}/${action}`;
  const resp = await fetch(`${baseUrl()}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "access-token": token,
      "Content-Type": "application/json",
    },
    body: "{}",
    cache: "no-store",
  });
  let body: PlugCommandResponse | null = null;
  try {
    body = (await resp.json()) as PlugCommandResponse;
  } catch {
    /* fall through */
  }
  if (!resp.ok) {
    const detail = body?.message ?? resp.statusText;
    throw new PlugsApiError(detail, resp.status);
  }
  if (!body) throw new PlugsApiError("empty response", resp.status);
  return body;
}
